import { PipelineRunStatus } from '../constants/pipelineRunStatus';

// AC-12 / R2 (pre-impl review) — pins a Failed run to the Build or Deploy node using the
// durable `ImageId` signal, never the live status transition (which cannot localize a
// cold-loaded Failed run: both a build failure and a deploy failure land on the same
// terminal `Status=Failed`). `PipelineRunDto.ImageId` is stamped only on build success
// (BE: DTOs/PipelineRunDto.cs:19), so:
//   Failed + ImageId present  -> the build succeeded, so the Deploy step is what failed.
//   Failed + ImageId absent   -> the build itself never produced an image, so it failed.
// This is durable across a cold deep-link/refresh (not just a live-observed transition) and
// keeps the slice FE-only (no BE change).
//
// Non-Failed statuses derive the two stage nodes' progress for the linear flow (AC-6/AC-9):
// Queued -> nothing started yet; Building -> build in progress; Deploying -> build done,
// deploy in progress; Succeeded -> both done. `wholeFlowFailed` is the coarse fallback the
// spec calls for "only if neither [node] is derivable" — with a fixed 1-5 numeric enum and
// ImageId always either present or absent, this only fires for a value outside the known
// enum, so it never silently implies success.
export const deriveRunFlowNodeStatus = run => {
	if (!run) {
		return { build: 'pending', deploy: 'pending', wholeFlowFailed: false };
	}

	switch (run.Status) {
		case PipelineRunStatus.Queued:
			return { build: 'pending', deploy: 'pending', wholeFlowFailed: false };
		case PipelineRunStatus.Building:
			return { build: 'active', deploy: 'pending', wholeFlowFailed: false };
		case PipelineRunStatus.Deploying:
			return { build: 'success', deploy: 'active', wholeFlowFailed: false };
		case PipelineRunStatus.Succeeded:
			return { build: 'success', deploy: 'success', wholeFlowFailed: false };
		case PipelineRunStatus.Failed: {
			const hasImage = run.ImageId !== undefined && run.ImageId !== null && run.ImageId !== '';
			return hasImage ? { build: 'success', deploy: 'failed', wholeFlowFailed: false } : { build: 'failed', deploy: 'pending', wholeFlowFailed: false };
		}
		// deploy-pipelines-run-cancel (Wave 5, AC-23) — an EXPLICIT arm, not the `default` arm
		// below (which returns wholeFlowFailed: true and is exactly the trap this AC exists to
		// close: without this arm, every cancelled run would render as a failed flow, the very
		// misreporting a distinct sixth status was chosen to avoid).
		//
		// Epic-wide adversarial review (M1) — this arm originally returned `pending` for BOTH
		// stages, on the reasoning that a cancel can land before or during the build so neither
		// stage can be claimed successful. True, but `pending` does not mean "we can't say", it
		// means "Not started" — and it drove two visible falsehoods for the DOMINANT cancel case
		// (cancelling a run that has been building for minutes): the timeline read "Build image ·
		// Not started" for a build that demonstrably ran, and `PipelineFlow`'s
		// `stage.status !== 'pending'` log-panel guard unmounted the build log in the same commit,
		// removing the only route to output the API was still serving.
		//
		// A cancel is a THIRD outcome, so the BUILD stage gets its own state rather than borrowing
		// `pending`. Which state is derived from a durable artifact field, never from the live
		// transition, so it survives a cold load:
		//   RunId present -> a registry build was started (RunId is stamped only at the flip to
		//                    Building, or by the trigger's own concurrency handler when a cancel
		//                    beat it) and did not finish -> Build is what was cancelled.
		//   RunId absent  -> cancelled while still Queued, before any build existed. `pending` is
		//                    the HONEST answer: nothing started, and there is no log to offer, so
		//                    the panel correctly stays absent.
		//
		// DEPLOY is always `pending` here, and that is not an omission. An independent review of
		// the first version of this fix established that **a Cancelled run can never carry an
		// ImageId**: `run.ImageId` is written at exactly one site (PipelineRunHostedService, in the
		// same save as the Building -> Deploying flip) and a run that reaches it is `Deploying`,
		// which is not in the BE's CancellableStatuses. An earlier draft branched on ImageId to
		// credit the build and mark the deploy cancelled; that branch was unreachable, so it was
		// dead code carrying two tests that could never fail. If the deploy leg ever becomes
		// cancellable, THAT is when this arm grows a second branch — and the BE change will make it
		// reachable and testable at the same time.
		case PipelineRunStatus.Cancelled: {
			const buildWasStarted = run.RunId !== undefined && run.RunId !== null && run.RunId !== '';
			return buildWasStarted
				? { build: 'cancelled', deploy: 'pending', wholeFlowFailed: false }
				: { build: 'pending', deploy: 'pending', wholeFlowFailed: false };
		}
		default:
			return { build: 'pending', deploy: 'pending', wholeFlowFailed: true };
	}
};

export default deriveRunFlowNodeStatus;
